import React, { useState, useEffect, useCallback } from "react";
import { botAnalyticsAPI } from "../services/api";
import { RefreshCw, Activity, Shield, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, XCircle, Clock, BarChart3 } from "lucide-react";

const SEVERITY_STYLES = {
  success: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
  warning: "bg-amber-500/10 border-amber-500/30 text-amber-400",
  error: "bg-red-500/10 border-red-500/30 text-red-400",
  info: "bg-slate-500/10 border-slate-500/30 text-slate-400",
};

const SEVERITY_ICONS = {
  success: <CheckCircle className="w-3.5 h-3.5" />,
  warning: <AlertTriangle className="w-3.5 h-3.5" />,
  error: <XCircle className="w-3.5 h-3.5" />,
  info: <Clock className="w-3.5 h-3.5" />,
};

export default function BotAnalytics() {
  const [summary, setSummary] = useState(null);
  const [recap, setRecap] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("diagnostics");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryData, recapData, compData] = await Promise.all([
        botAnalyticsAPI.getSummary().catch(() => null),
        botAnalyticsAPI.getDailyRecap().catch(() => null),
        botAnalyticsAPI.getGptVsClaude().catch(() => null),
      ]);
      if (summaryData) setSummary(summaryData);
      if (recapData) setRecap(recapData);
      if (compData) setComparison(compData);
    } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!summary && !recap) {
    return (
      <div className="glass-card rounded-xl p-4 border border-slate-700/30 animate-pulse" data-testid="bot-analytics-loading">
        <div className="h-4 bg-slate-700/50 rounded w-1/3 mb-3" />
        <div className="h-20 bg-slate-700/30 rounded" />
      </div>
    );
  }

  const allTime = summary?.all_time || {};
  const protections = summary?.protections || {};
  const diagnostics = summary?.recent_diagnostics || [];
  const overexposed = summary?.overexposed_tickers || {};
  const daily = summary?.daily_breakdown || {};

  return (
    <div className="space-y-3" data-testid="bot-analytics-panel">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-cyan-400" />
          <h3 className="text-sm font-black text-white uppercase tracking-wider">Bot Analytics</h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-slate-700/30">
            {["diagnostics", "comparison", "recap", "metrics"].map((s) => (
              <button
                key={s}
                onClick={() => setActiveSection(s)}
                className={`px-2.5 py-1 text-[10px] font-bold transition-all ${
                  activeSection === s
                    ? "bg-cyan-500/20 text-cyan-400"
                    : "bg-slate-800/50 text-slate-500 hover:text-slate-300"
                }`}
                data-testid={`analytics-tab-${s}`}
              >
                {s === "diagnostics" ? "Diagnostics" : s === "comparison" ? "GPT vs Claude vs Dual" : s === "recap" ? "Recap Jour" : "Metriques"}
              </button>
            ))}
          </div>
          <button onClick={fetchData} disabled={loading} className="p-1 rounded bg-slate-700/50 text-slate-400 hover:text-white disabled:opacity-40" data-testid="refresh-analytics">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* QUICK STATS BAR */}
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
        <StatBadge label="Win Rate" value={`${allTime.win_rate || 0}%`} color={allTime.win_rate >= 55 ? "emerald" : allTime.win_rate >= 45 ? "amber" : "red"} />
        <StatBadge label="Avg Win" value={`+${allTime.avg_win_pct || 0}%`} color="emerald" />
        <StatBadge label="Avg Loss" value={`${allTime.avg_loss_pct || 0}%`} color="red" />
        <StatBadge label="Echecs" value={`${allTime.failed_rate || 0}%`} color={allTime.failed_rate <= 10 ? "emerald" : allTime.failed_rate <= 20 ? "amber" : "red"} />
        <StatBadge label="C1" value={protections.c1_blocks || 0} color="cyan" icon={<Shield className="w-2.5 h-2.5" />} />
        <StatBadge label="C2" value={protections.c2_blocks || 0} color="cyan" icon={<Shield className="w-2.5 h-2.5" />} />
        <StatBadge label="C3" value={protections.c3_triggers || 0} color="amber" icon={<AlertTriangle className="w-2.5 h-2.5" />} />
        <StatBadge label="C4" value={protections.c4_extensions || 0} color="violet" icon={<Activity className="w-2.5 h-2.5" />} />
      </div>

      {/* ACTIVE SECTION */}
      {activeSection === "diagnostics" && (
        <DiagnosticsSection diagnostics={diagnostics} />
      )}

      {activeSection === "comparison" && (
        <ComparisonSection comparison={comparison} />
      )}

      {activeSection === "recap" && (
        <RecapSection recap={recap} />
      )}

      {activeSection === "metrics" && (
        <MetricsSection allTime={allTime} daily={daily} overexposed={overexposed} />
      )}
    </div>
  );
}

function StatBadge({ label, value, color, icon }) {
  const colors = {
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    red: "text-red-400 bg-red-500/10 border-red-500/20",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    cyan: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
    violet: "text-violet-400 bg-violet-500/10 border-violet-500/20",
  };
  return (
    <div className={`rounded-lg px-2 py-1.5 border text-center ${colors[color] || colors.cyan}`}>
      <div className="text-[8px] text-slate-500 uppercase">{label}</div>
      <div className="flex items-center justify-center gap-1">
        {icon}
        <span className="text-xs font-black">{value}</span>
      </div>
    </div>
  );
}

function ComparisonSection({ comparison }) {
  if (!comparison) {
    return (
      <div className="glass-card rounded-lg p-4 border border-slate-700/30 text-center">
        <Activity className="w-6 h-6 text-slate-600 mx-auto mb-2" />
        <p className="text-xs text-slate-500">Chargement du comparatif...</p>
      </div>
    );
  }

  const gpt = comparison.gpt || {};
  const claude = comparison.claude || {};
  const dual = comparison.dual_brain || {};

  const eras = [
    { data: gpt, color: "violet", label: gpt.label || "GPT-5.4" },
    { data: claude, color: "cyan", label: claude.label || "Claude Sonnet" },
    { data: dual, color: "amber", label: dual.label || "Dual Brain" },
  ].filter(e => e.data.total_trades > 0);

  const metrics = [
    { label: "Win Rate", key: "win_rate", suffix: "%", better: "higher", icon: <TrendingUp className="w-3 h-3" /> },
    { label: "Trades/Jour", key: "trades_per_day", suffix: "", better: "lower", icon: <Activity className="w-3 h-3" /> },
    { label: "Avg Win", key: "avg_win_pct", suffix: "%", better: "higher", prefix: "+" },
    { label: "Avg Loss", key: "avg_loss_pct", suffix: "%", better: "higher" },
    { label: "P&L Total", key: "total_pnl_pct", suffix: "%", better: "higher" },
    { label: "Taux Echec", key: "failed_rate", suffix: "%", better: "lower", icon: <AlertTriangle className="w-3 h-3" /> },
    { label: "Jours", key: "days_active", suffix: "j", better: "none" },
    { label: "Total Trades", key: "total_trades", suffix: "", better: "none" },
  ];

  const colorMap = { violet: { border: "border-violet-500/30", bg: "bg-violet-500/5", dot: "bg-violet-400", text: "text-violet-400", bar: "bg-violet-500/50" },
                     cyan: { border: "border-cyan-500/30", bg: "bg-cyan-500/5", dot: "bg-cyan-400", text: "text-cyan-400", bar: "bg-cyan-500/50" },
                     amber: { border: "border-amber-500/30", bg: "bg-amber-500/5", dot: "bg-amber-400", text: "text-amber-400", bar: "bg-amber-500/50" } };

  return (
    <div className="space-y-3" data-testid="comparison-section">
      {/* Header cards */}
      <div className={`grid gap-3 ${eras.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
        {eras.map(({ data, color, label }) => {
          const c = colorMap[color];
          return (
            <div key={color} className={`rounded-xl p-3 border ${c.border} ${c.bg}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-2 h-2 rounded-full ${c.dot}`} />
                <span className={`text-[10px] font-black ${c.text} uppercase`}>{label}</span>
              </div>
              <div className="text-[8px] text-slate-500">{data.period || "?"}</div>
              <div className="mt-2 grid grid-cols-3 gap-1">
                <div>
                  <div className="text-[7px] text-slate-500">Trades</div>
                  <div className="text-xs font-black text-white">{data.total_trades || 0}</div>
                </div>
                <div>
                  <div className="text-[7px] text-slate-500">Win Rate</div>
                  <div className={`text-xs font-black ${(data.win_rate || 0) >= 50 ? "text-emerald-400" : "text-red-400"}`}>{data.win_rate || 0}%</div>
                </div>
                <div>
                  <div className="text-[7px] text-slate-500">P&L</div>
                  <div className={`text-xs font-black ${(data.total_pnl_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>{(data.total_pnl_pct || 0) >= 0 ? "+" : ""}{data.total_pnl_pct || 0}%</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Metric-by-metric comparison */}
      <div className="glass-card rounded-xl p-3 border border-slate-700/30">
        <div className="text-[10px] font-bold text-white mb-3 uppercase">Comparaison detaillee</div>
        <div className="space-y-2">
          {metrics.map(({ label, key, suffix, better, prefix }) => {
            const vals = eras.map(e => e.data[key] || 0);
            const bestVal = better === "higher" ? Math.max(...vals) : better === "lower" ? Math.min(...vals) : null;

            return (
              <div key={key} className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500 w-20 flex-shrink-0">{label}</span>
                <div className="flex-1 flex items-center gap-1">
                  {eras.map(({ data, color }, i) => {
                    const v = data[key] || 0;
                    const isBest = bestVal !== null && v === bestVal && vals.filter(x => x === bestVal).length === 1;
                    const c = colorMap[color];
                    return (
                      <span key={i} className={`text-[10px] font-bold flex-1 text-center ${isBest ? c.text : "text-slate-400"}`}>
                        {prefix && v > 0 ? prefix : ""}{v}{suffix}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Verdict */}
      {dual.total_trades > 0 ? (
        <div className="rounded-xl p-3 border border-amber-500/30 bg-amber-500/5">
          <div className="text-[10px] font-bold text-white mb-1 uppercase">Dual Brain Actif</div>
          <p className="text-[11px] text-slate-300">
            GPT-5 propose les trades (74% precision), Claude valide le risque. 
            {dual.total_trades} trades executes. 
            {dual.days_active <= 1 && " Donnees encore limitees — revenez dans quelques jours."}
            {dual.win_rate > 0 && ` Win rate actuel: ${dual.win_rate}%.`}
          </p>
        </div>
      ) : (
        <div className="rounded-xl p-3 border border-amber-500/30 bg-amber-500/5">
          <div className="text-[10px] font-bold text-white mb-1 uppercase">Dual Brain (GPT + Claude)</div>
          <p className="text-[11px] text-slate-300">
            Architecture Dual Brain activee — GPT-5 analyse et propose, Claude valide le risque. 
            Les premiers trades apparaitront bientot.
          </p>
        </div>
      )}
    </div>
  );
}

function DiagnosticsSection({ diagnostics }) {
  if (!diagnostics || diagnostics.length === 0) {
    return (
      <div className="glass-card rounded-lg p-4 border border-slate-700/30 text-center">
        <Activity className="w-6 h-6 text-slate-600 mx-auto mb-2" />
        <p className="text-xs text-slate-500">Aucun diagnostic encore. Les diagnostics apparaitront apres les prochains trades du bot.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5 max-h-[300px] overflow-y-auto scrollbar-thin" data-testid="diagnostics-feed">
      {diagnostics.map((d, i) => {
        const style = SEVERITY_STYLES[d.severity] || SEVERITY_STYLES.info;
        const icon = SEVERITY_ICONS[d.severity] || SEVERITY_ICONS.info;
        const ts = d.timestamp ? new Date(d.timestamp).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "";
        
        return (
          <div key={i} className={`flex items-start gap-2 rounded-lg px-3 py-2 border ${style}`} data-testid={`diagnostic-${i}`}>
            <div className="mt-0.5 flex-shrink-0">{icon}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold">{d.ticker}</span>
                {d.tags?.map((tag) => (
                  <span key={tag} className="text-[8px] px-1 py-0.5 rounded bg-slate-800/50 text-slate-500">{tag}</span>
                ))}
                <span className="text-[9px] text-slate-600 ml-auto flex-shrink-0">{ts}</span>
              </div>
              <p className="text-[11px] mt-0.5">{d.message}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RecapSection({ recap }) {
  if (!recap || !recap.recap) {
    return (
      <div className="glass-card rounded-lg p-4 border border-slate-700/30 text-center">
        <p className="text-xs text-slate-500">Pas de recap disponible pour aujourd'hui.</p>
      </div>
    );
  }

  const stats = recap.stats || {};
  
  return (
    <div className="space-y-3" data-testid="daily-recap-section">
      {/* Stats grid */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <MiniStat label="Cycles" value={stats.total_cycles || 0} />
        <MiniStat label="NO_SETUP" value={`${stats.no_setup_rate || 0}%`} color={stats.no_setup_rate > 70 ? "red" : "slate"} />
        <MiniStat label="Trades" value={stats.total_trades || 0} />
        <MiniStat label="Win Rate" value={`${stats.win_rate || 0}%`} color={stats.win_rate >= 55 ? "emerald" : "amber"} />
        <MiniStat label="P&L" value={`${(stats.total_pnl_pct || 0) >= 0 ? "+" : ""}${stats.total_pnl_pct || 0}%`} color={stats.total_pnl_pct >= 0 ? "emerald" : "red"} />
        <MiniStat label="Echecs" value={stats.failed || 0} color={stats.failed > 3 ? "red" : "slate"} />
      </div>

      {/* Recap text */}
      <div className="glass-card rounded-lg p-3 border border-slate-700/30">
        <pre className="text-[11px] text-slate-300 whitespace-pre-wrap font-mono leading-relaxed">{recap.recap}</pre>
      </div>

      {/* Recommendations */}
      {recap.recommendations && recap.recommendations.length > 0 && (
        <div className="rounded-lg p-3 border border-cyan-500/20 bg-cyan-500/5">
          <div className="text-[10px] font-bold text-cyan-400 mb-1.5 uppercase">Recommandations IA</div>
          {recap.recommendations.map((r, i) => (
            <div key={i} className="flex items-start gap-2 mb-1">
              <TrendingUp className="w-3 h-3 text-cyan-400 mt-0.5 flex-shrink-0" />
              <span className="text-[11px] text-cyan-300">{r}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, color = "slate" }) {
  const textColors = { emerald: "text-emerald-400", red: "text-red-400", amber: "text-amber-400", slate: "text-white" };
  return (
    <div className="bg-slate-800/40 rounded-lg px-2 py-1.5 text-center">
      <div className="text-[8px] text-slate-500 uppercase">{label}</div>
      <div className={`text-sm font-black ${textColors[color] || textColors.slate}`}>{value}</div>
    </div>
  );
}

function MetricsSection({ allTime, daily, overexposed }) {
  const days = Object.entries(daily).slice(0, 7);
  
  return (
    <div className="space-y-3" data-testid="metrics-section">
      {/* All-time metrics */}
      <div className="glass-card rounded-lg p-3 border border-slate-700/30">
        <div className="text-[10px] font-bold text-white mb-2 uppercase">Performance globale</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <div className="text-[9px] text-slate-500">Trades total</div>
            <div className="text-lg font-black text-white">{allTime.total_trades || 0}</div>
          </div>
          <div>
            <div className="text-[9px] text-slate-500">Win / Loss</div>
            <div className="text-lg font-black">
              <span className="text-emerald-400">{allTime.wins || 0}W</span>
              <span className="text-slate-600 mx-1">/</span>
              <span className="text-red-400">{allTime.losses || 0}L</span>
            </div>
          </div>
          <div>
            <div className="text-[9px] text-slate-500">Ratio Gain/Perte</div>
            <div className={`text-lg font-black ${Math.abs(allTime.avg_win_pct || 0) > Math.abs(allTime.avg_loss_pct || 0) ? "text-emerald-400" : "text-red-400"}`}>
              {allTime.avg_loss_pct ? (Math.abs(allTime.avg_win_pct || 0) / Math.abs(allTime.avg_loss_pct)).toFixed(1) : "N/A"}:1
            </div>
          </div>
          <div>
            <div className="text-[9px] text-slate-500">P&L cumule</div>
            <div className={`text-lg font-black ${(allTime.total_pnl_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {(allTime.total_pnl_pct || 0) >= 0 ? "+" : ""}{allTime.total_pnl_pct || 0}%
            </div>
          </div>
        </div>
      </div>

      {/* Daily breakdown */}
      {days.length > 0 && (
        <div className="glass-card rounded-lg p-3 border border-slate-700/30">
          <div className="text-[10px] font-bold text-white mb-2 uppercase">Par jour</div>
          <div className="space-y-1">
            {days.map(([date, d]) => (
              <div key={date} className="flex items-center gap-3 text-[11px]">
                <span className="text-slate-500 w-20 font-mono">{date.slice(5)}</span>
                <span className="text-slate-400 w-16">{d.trades} trades</span>
                <span className="text-emerald-400 w-8">{d.wins}W</span>
                <span className="text-red-400 w-8">{d.losses}L</span>
                <span className="text-red-400/60 w-8">{d.failed}F</span>
                <span className={`font-bold ${d.pnl_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {d.pnl_pct >= 0 ? "+" : ""}{d.pnl_pct.toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overexposed tickers */}
      {Object.keys(overexposed).length > 0 && (
        <div className="rounded-lg p-3 border border-amber-500/20 bg-amber-500/5">
          <div className="text-[10px] font-bold text-amber-400 mb-1.5 uppercase">Tickers surexposes (avant C2)</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(overexposed).map(([ticker, data]) => (
              <span key={ticker} className="text-[10px] px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300">
                {ticker}: {data.buys} achats {data.failed > 0 ? `+ ${data.failed} echecs` : ""}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
